import React from 'react';
import { motion } from 'framer-motion';

import FadeIn from '../../../components/animations/FadeIn';

// "Zero Section" — sits immediately under the hero. Giant $0 graphic,
// the "you don't pay until your roof is on" headline, three columns
// reinforcing the $0-at-each-stage promise, and a CTA back to the
// hero's address form.
//
// Visual character mirrors the hero (dark navy bg, blue-glow accents)
// so the transition from hero to this section reads as visual continuation.
//
// Reveal animations use the shared FadeIn component
// (src/components/animations/FadeIn.jsx). Scroll-triggered via whileInView
// because users scroll DOWN to reach this section.

const ZeroSection = () => {
  // Targeted smooth-scroll for THIS CTA only. The codebase's global
  // `html { scroll-behavior: auto }` (tailwind.css:940) is a deliberate
  // choice — we don't override it. JS handler scopes the smooth behavior
  // to the zero-cta button alone.
  const handleCtaClick = (e) => {
    e.preventDefault();
    const target = document.getElementById('hero-form');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <section className="zero-section">
      <div className="container">
        <FadeIn className="zero-eyebrow">The OpenbooQ Promise</FadeIn>

        {/* Reactive cursor-hover glow on the giant $0. At rest, the base
            drop-shadow matches the .zero-graphic CSS default (40px blur,
            0.4 alpha). On cursor hover anywhere over the element's huge
            bounding box, the glow intensifies (80px blur, 0.85 alpha).
            Smooth ease-out in both directions. Touch devices skip
            whileHover entirely — the element stays at the resting state
            so no broken touch behavior. */}
        <motion.div
          className="zero-graphic"
          initial={{ opacity: 0, y: 24, filter: 'drop-shadow(0 12px 40px rgba(7, 176, 233, 0.4))' }}
          whileInView={{
            opacity: 1,
            y: 0,
            filter: 'drop-shadow(0 12px 40px rgba(7, 176, 233, 0.4))',
            transition: { duration: 0.6, delay: 0.08, ease: [0.16, 1, 0.3, 1] },
          }}
          whileHover={{
            filter: 'drop-shadow(0 16px 80px rgba(7, 176, 233, 0.85))',
            transition: { duration: 0.5, ease: 'easeOut' },
          }}
          viewport={{ once: true, amount: 0.2 }}
        >
          $0
        </motion.div>

        <FadeIn as="h2" className="zero-headline" delay={0.16}>
          You don't pay until your roof is on.
        </FadeIn>

        <FadeIn as="p" className="zero-subhead" delay={0.24}>
          Not at signing. Not when materials are ordered. Not until you walk outside, inspect the work, and approve it.
          No deposit. No escrow. No catch.
        </FadeIn>

        <div className="zero-columns">
          <FadeIn delay={0.08}>
            <motion.div
              className="zero-column"
              whileHover={{ y: -4, scale: 1.02 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <div className="col-num">$0</div>
              <h3>At Signing</h3>
              <p>You sign the contract. No payment, no deposit, no card required. We don't ask for a dollar at this step.</p>
            </motion.div>
          </FadeIn>

          {/* Flow line: draws left-to-right AFTER the first card has revealed.
              Grid slot 2 of the 5-col template (1fr auto 1fr auto 1fr). */}
          <motion.div
            className="zero-flow-line"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
            aria-hidden="true"
          />

          <FadeIn delay={0.16}>
            <motion.div
              className="zero-column"
              whileHover={{ y: -4, scale: 1.02 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <div className="col-num">$0</div>
              <h3>At Material Order</h3>
              <p>We order your materials and lock in your install date. Still no payment from you.</p>
            </motion.div>
          </FadeIn>

          {/* Flow line: draws after the second card. */}
          <motion.div
            className="zero-flow-line"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.7, ease: 'easeOut' }}
            aria-hidden="true"
          />

          <FadeIn delay={0.24}>
            <motion.div
              className="zero-column"
              whileHover={{ y: -4, scale: 1.02 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <div className="col-num">$0</div>
              <h3>Until It's Done</h3>
              <p>You inspect the finished roof. Only then — when you approve — does payment process.</p>
            </motion.div>
          </FadeIn>
        </div>

        <FadeIn className="zero-cta-wrap" delay={0.32}>
          <motion.a
            href="#hero-form"
            className="zero-cta"
            onClick={handleCtaClick}
            whileHover={{ y: -2, boxShadow: '0 16px 40px rgba(7, 176, 233, 0.6)' }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            See your $0-down price
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </motion.a>
        </FadeIn>
      </div>
    </section>
  );
};

export default ZeroSection;
